import type { Match, MatchStatus } from '@api/useSupplierReconciliation/types/match'
import { useLocalStorage } from '@gravitate-js/excalibrr'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useSupplierReconciliation } from '../../../../api/useSupplierReconciliation'
import { useSecurityContext } from '../../../Navigation/SecurityContext'
import { useMatchStorage } from './useMatchStorage'

// Type for status counts
interface StatusCounts {
  all: number
  unapproved: number
  unmatched: number
  approved: number
  hold: number
  unmapped: number
  void: number
  [key: string]: number
}

// Extended type for status counts with loading states
interface StatusCountsWithLoading extends StatusCounts {
  isLoading: { [key: string]: boolean }
}

type UseMatchViewParams = {}

export function useMatchView(_params: UseMatchViewParams = {}) {
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const { user } = useSecurityContext()

  // --- State Management --- //

  // Filter State
  const { value: activeStatus, setValue: setActiveStatus } = useLocalStorage<MatchStatus | 'all'>(
    'match-view-status',
    'all'
  )

  // UI State - declare early so it can be used in date range logic
  const [selectedRows, setSelectedRows] = useState<Match[]>([])
  const [isVoidedViewActive, setIsVoidedViewActive] = useState<boolean>(false)

  // Use storage hook for all date range and storage management
  const {
    dateRange,
    dateFilters,
    setDateFilters,
    dayRangeStorage,
    setDayRangeStorage,
    dateRangesLoaded,
    // Import specific date ranges for API calls
    approvedDateRange,
    voidedDateRange,
    otherTabsDateRange,
    // Import specific setters for conditional logic
    setApprovedDateRange,
    setVoidedDateRange,
    setOtherTabsDateRange,
    // Expose default helpers for reset functionality
    getDefaultApprovedDayRangeStorage,
    getDefaultVoidedDayRangeStorage,
    getDefaultOtherTabsDayRangeStorage,
    getDefaultApprovedDateRange,
    getDefaultVoidedDateRange,
    getDefaultDateRange,
  } = useMatchStorage({
    isVoidedViewActive,
    activeStatus,
    hasUrlDateRange: searchParams.getAll('date_range[]').length === 2,
  })

  // Note: DateRangeFilter synchronization is handled by the setDayRangeStorage persistent functions
  // which update both the slider values and corresponding date ranges automatically

  const setDateRange = isVoidedViewActive
    ? setVoidedDateRange
    : activeStatus === 'approved'
      ? setApprovedDateRange
      : setOtherTabsDateRange

  const [serverFilters, setServerFilters] = useState<{
    supplier_name?: string
    invoice_number?: string
    past_due?: boolean
    terminal?: string
  }>({
    supplier_name: undefined,
    invoice_number: undefined,
    past_due: undefined,
    terminal: undefined,
  })
  const [invoiceDueFilter, setInvoiceDueFilter] = useState<string | null>(null)

  const { value: assignedToMe, setValue: setAssignedToMe } = useLocalStorage<boolean>(
    'match-view-assigned-to-me',
    false
  )

  // --- Utility State --- //
  const prevActiveStatus = useRef<MatchStatus | 'all' | null>(activeStatus)
  const urlCleanedRef = useRef<boolean>(false)

  // --- Effects --- //

  // Initialize state from URL Search Params
  useEffect(() => {
    const urlStatus = searchParams.get('status')
    const urlSupplierName = searchParams.get('supplier_name')
    const urlAssignedToMe = searchParams.get('assigned_to_me')
    const urlInvoiceNumber = searchParams.get('invoice_number')
    const urlTab = searchParams.get('tab')
    const urlPastDue = searchParams.get('past_due')
    const urlInvoiceDueFilter = searchParams.get('invoice_due_filter')
    const urlTerminalName = searchParams.get('terminal_name')
    const urlDateRanges = searchParams.getAll('date_range[]')

    // Reset cleanup ref if we have new URL parameters (new navigation)
    if (searchParams.toString() && !urlCleanedRef.current) {
      // Fresh navigation with parameters - allow cleanup later
    } else if (!searchParams.toString()) {
      // URL was cleaned - reset for next navigation
      urlCleanedRef.current = false
    }

    // Handle status from URL
    if (urlStatus) {
      setActiveStatus(urlStatus as MatchStatus | 'all')
    }

    // Handle tab parameter - map tab names to status
    if (urlTab && !urlStatus) {
      const tabStatusMap: Record<string, MatchStatus | 'all'> = {
        all_open: 'all',
        unmatched: 'unmatched',
        on_hold: 'hold',
        unapproved: 'unapproved',
        approved: 'approved',
      }

      const mappedStatus = tabStatusMap[urlTab]
      if (mappedStatus) {
        setActiveStatus(mappedStatus)
      }
    }

    // Handle date range from URL parameters - PRIORITY over stored settings
    if (urlDateRanges.length === 2) {
      const startDate = new Date(urlDateRanges[0])
      const endDate = new Date(urlDateRanges[1])

      // Only update if dates are valid
      if (!isNaN(startDate.getTime()) && !isNaN(endDate.getTime())) {
        // Force update ALL date ranges to URL values to ensure consistency
        // This ensures navigation from dashboard works correctly regardless of tab
        setOtherTabsDateRange([startDate, endDate])
        setApprovedDateRange([startDate, endDate])
        setVoidedDateRange([startDate, endDate])
      }
    }

    // Handle server filters
    const newServerFilters: { supplier_name?: string; invoice_number?: string; past_due?: boolean; terminal?: string } =
      {}

    if (urlSupplierName) {
      newServerFilters.supplier_name = urlSupplierName
    }

    if (urlInvoiceNumber) {
      newServerFilters.invoice_number = urlInvoiceNumber
    }

    if (urlPastDue === 'true') {
      newServerFilters.past_due = true
    }

    if (urlTerminalName) {
      newServerFilters.terminal = urlTerminalName
    }

    // Only set filter if URL has the parameter, don't reset when it's missing
    if (urlInvoiceDueFilter === 'overdue') {
      setInvoiceDueFilter('overdue')
    }

    // Only update if there are actual filters to set
    if (Object.keys(newServerFilters).length > 0) {
      setServerFilters((prevFilters) => ({ ...prevFilters, ...newServerFilters }))
    }

    if (urlAssignedToMe === 'true') {
      setAssignedToMe(true)
    } else if (urlAssignedToMe === 'false') {
      setAssignedToMe(false)
    }

    // Clean up URL after parameters are processed, but only once per navigation
    if (!urlCleanedRef.current && searchParams.toString()) {
      urlCleanedRef.current = true
      setTimeout(() => {
        setSearchParams({}, { replace: true })
      }, 300) // Shorter timeout for faster cleanup
    }
  }, [searchParams, setActiveStatus, setAssignedToMe, isVoidedViewActive, navigate, setSearchParams])

  // Clean up URL parameters when activeStatus changes (tab switching)
  useEffect(() => {
    const currentParams = new URLSearchParams(searchParams.toString())
    let needsUpdate = false

    // When switching tabs, clean up conflicting parameters
    if (prevActiveStatus.current !== activeStatus && prevActiveStatus.current !== null) {
      // Clean up invoice_due_filter from URL when switching tabs, but preserve the state
      // since overdue filter is valid across all tabs
      if (currentParams.has('invoice_due_filter')) {
        currentParams.delete('invoice_due_filter')
        needsUpdate = true
        // Note: Not clearing setInvoiceDueFilter(null) here to preserve cross-tab filtering
      }

      // Update tab parameter to match active status
      const tabMap: Record<MatchStatus | 'all', string> = {
        all: 'all_open',
        unmatched: 'unmatched',
        hold: 'on_hold',
        unapproved: 'unapproved',
        approved: 'approved',
        unmapped: 'unmapped',
        void: 'void',
      }

      const newTabValue = tabMap[activeStatus]
      if (newTabValue && currentParams.get('tab') !== newTabValue) {
        currentParams.set('tab', newTabValue)
        needsUpdate = true
      }

      // Update URL without causing a navigation
      if (needsUpdate) {
        setSearchParams(currentParams, { replace: true })
      }
    }
  }, [activeStatus, searchParams, setSearchParams])

  // Initialize default date ranges only if they haven't been set by user
  useEffect(() => {
    prevActiveStatus.current = activeStatus
  }, [activeStatus])

  // --- Data Fetching --- //

  // Build date ranges following the feedback pattern
  const openDateRange = useMemo(() => {
    // Always fetch open data to ensure accurate tab counts
    // Use other tabs date range for open data, fallback to proper defaults
    if (otherTabsDateRange?.[0] && otherTabsDateRange?.[1]) {
      return { start: otherTabsDateRange[0], end: otherTabsDateRange[1] }
    }
    // Use proper default function for consistency
    const [start, end] = getDefaultDateRange()
    return { start, end }
  }, [otherTabsDateRange])

  const approvedDateRangeForAPI = useMemo(() => {
    // Always fetch approved data to ensure accurate tab counts
    // Use approved tab's specific date range, fallback to proper defaults
    if (approvedDateRange?.[0] && approvedDateRange?.[1]) {
      return { start: approvedDateRange[0], end: approvedDateRange[1] }
    }
    // Use proper default function for consistency
    const [start, end] = getDefaultApprovedDateRange()
    return { start, end }
  }, [approvedDateRange])

  const voidedDateRangeForAPI = useMemo(() => {
    // Always fetch voided data to ensure accurate button counts
    // Use voided tab's specific date range, fallback to proper defaults
    if (voidedDateRange?.[0] && voidedDateRange?.[1]) {
      return { start: voidedDateRange[0], end: voidedDateRange[1] }
    }
    // Use proper default function for consistency (90 days)
    const [start, end] = getDefaultVoidedDateRange()
    return { start, end }
  }, [voidedDateRange])

  // Build filters object - pass serverFilters to API for server-side filtering
  const filters = useMemo(() => {
    const baseFilters: Record<string, any> = {}

    // Include server filters for API query filtering
    // These filters come from the query filters drawer in the grid
    if (serverFilters) {
      Object.entries(serverFilters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          baseFilters[key] = value
        }
      })
    }

    // Note: assignedToMe and some specific filters are handled client-side for better UX

    return baseFilters
  }, [serverFilters])

  // Get API hooks - use separate hooks for each tab to prevent cross-tab recalculation
  const { useMatchOverviewOpen, useMatchOverviewApproved, useMatchOverviewVoided } = useSupplierReconciliation()

  // Independent queries for each tab - only recalculate when their specific date range changes
  const openQuery = useMatchOverviewOpen({
    dateRange: openDateRange,
    filters,
    enabled: dateRangesLoaded,
  })

  const approvedQuery = useMatchOverviewApproved({
    dateRange: approvedDateRangeForAPI,
    filters,
    enabled: dateRangesLoaded,
  })

  const voidedQuery = useMatchOverviewVoided({
    dateRange: voidedDateRangeForAPI,
    filters,
    enabled: dateRangesLoaded,
  })

  // Get the current active query based on tab/view state
  const activeQuery = isVoidedViewActive ? voidedQuery : activeStatus === 'approved' ? approvedQuery : openQuery

  // Combine data from all queries for status counts (need all data for accurate counts)
  const combinedData = useMemo(() => {
    // Use the model config from any successful query (they should all be the same)
    const modelConfig = openQuery.data?.modelConfig || approvedQuery.data?.modelConfig || voidedQuery.data?.modelConfig

    // Combine all rows from all queries for status counts
    const allRows = [
      ...(openQuery.data?.rows || []),
      ...(approvedQuery.data?.rows || []),
      ...(voidedQuery.data?.rows || []),
    ]

    // Deduplicate by ID in case of overlaps
    const seenIds = new Set()
    const combinedRows = allRows.filter((row) => {
      if (seenIds.has(row.id)) {
        return false
      }
      seenIds.add(row.id)
      return true
    })

    return {
      rows: combinedRows,
      modelConfig,
      // Combine unmapped supplier names from all queries
      unmapped_supplier_names: [
        ...(openQuery.data?.unmapped_supplier_names || []),
        ...(approvedQuery.data?.unmapped_supplier_names || []),
        ...(voidedQuery.data?.unmapped_supplier_names || []),
      ].filter((name, index, arr) => arr.indexOf(name) === index), // deduplicate
    }
  }, [openQuery.data, approvedQuery.data, voidedQuery.data])

  // Create a matchQuery interface that uses active query for loading/display but combined data for counts
  const matchQuery = useMemo(() => {
    return {
      data: {
        // Use active query data for display
        ...activeQuery.data,
        // But use combined data for accurate status counts
        rows: activeQuery.data?.rows || [],
        modelConfig: combinedData.modelConfig,
        // Include combined unmapped suppliers for counts
        unmapped_supplier_names: combinedData.unmapped_supplier_names,
      },
      // Use active query loading state for responsive UI
      isLoading: activeQuery.isLoading,
      error: activeQuery.error,
      refetch: () => {
        openQuery.refetch()
        approvedQuery.refetch()
        voidedQuery.refetch()
      },
    }
  }, [activeQuery, combinedData])

  // --- Client-side Processing --- //

  // Apply client-side filtering (like assignedToMe and status filtering)
  const filteredData = useMemo(() => {
    if (!matchQuery.data) return null

    let rows = matchQuery.data.rows || []

    // Apply voided view filtering first (overrides status tabs)
    if (isVoidedViewActive) {
      rows = rows.filter((row: any) => row.status === 'void')
    } else {
      // Apply status filtering based on active tab
      if (activeStatus && activeStatus !== 'all') {
        rows = rows.filter((row: any) => row.status === activeStatus)
      } else if (activeStatus === 'all') {
        // "All Open" should exclude approved and void matches
        rows = rows.filter((row: any) => row.status !== 'approved' && row.status !== 'void')
      }
    }

    // Apply client-side filters for unmatched subcategories (only if server doesn't handle them)
    // Note: With server-side filtering now enabled, this may become redundant if the server
    // properly handles these subcategory filters. Keep for now as fallback.
    if (serverFilters?.match_status && typeof serverFilters.match_status === 'string') {
      // Handle unmatched subcategory filters client-side as fallback
      const matchStatus = serverFilters.match_status

      if (matchStatus === 'Unmatched') {
        // "Unmatched" -> items that have status of unmatched but have both invoice IDs and BOL IDs (broken records)
        rows = rows.filter((row: any) => {
          const hasInvoiceIds = row.invoice_numbers && row.invoice_numbers.some((inv: any) => inv.id)
          const hasBolIds = row.bol_numbers && row.bol_numbers.some((bol: any) => bol.id)
          return row.status === 'unmatched' && hasInvoiceIds && hasBolIds
        })
      } else if (matchStatus === 'Unmatched BOL') {
        // "Unmatched BOL" -> items that have status of unmatched and have BOL IDs but no invoice IDs
        rows = rows.filter((row: any) => {
          const hasInvoiceIds = row.invoice_numbers && row.invoice_numbers.some((inv: any) => inv.id)
          const hasBolIds = row.bol_numbers && row.bol_numbers.some((bol: any) => bol.id)
          return row.status === 'unmatched' && hasBolIds && !hasInvoiceIds
        })
      } else if (matchStatus === 'Unmatched Invoice') {
        // "Unmatched Invoice" -> items that have status of unmatched and have invoice IDs but no BOL IDs
        rows = rows.filter((row: any) => {
          const hasInvoiceIds = row.invoice_numbers && row.invoice_numbers.some((inv: any) => inv.id)
          const hasBolIds = row.bol_numbers && row.bol_numbers.some((bol: any) => bol.id)
          return row.status === 'unmatched' && hasInvoiceIds && !hasBolIds
        })
      }
    }

    // Apply assigned to me filter client-side
    if (assignedToMe && user?.username) {
      rows = rows.filter((row: any) => row.assignee?.username === user.username)
    }

    // Apply supplier name filter client-side (since API doesn't support it)
    if (serverFilters?.supplier_name) {
      rows = rows.filter((row: any) => {
        if (!row.supplier_name) return false
        return row.supplier_name.toLowerCase().includes(serverFilters.supplier_name!.toLowerCase())
      })
    }

    // Apply invoice number filter client-side (for Recent Activity navigation)
    if (serverFilters?.invoice_number) {
      rows = rows.filter((row: any) => {
        if (!row.invoice_numbers) return false
        return row.invoice_numbers.some((invoice: any) =>
          invoice.number?.toLowerCase().includes(serverFilters.invoice_number!.toLowerCase())
        )
      })
    }
    const result = {
      ...matchQuery.data,
      rows,
      params: matchQuery.data.params, // Keep params as array for serverParams
      total: rows.length, // Pass total separately
    }

    return result
  }, [matchQuery.data, assignedToMe, activeStatus, user?.username, isVoidedViewActive, serverFilters])

  // Calculate status counts with loading states - track which tabs are loading vs have data
  const statusCounts = useMemo((): StatusCountsWithLoading => {
    const defaultCounts: StatusCounts = {
      all: 0,
      unapproved: 0,
      unmatched: 0,
      approved: 0,
      hold: 0,
      unmapped: 0,
      void: 0,
    }

    // Track loading states for each count type
    const loadingStates = {
      all: openQuery.isLoading, // "all" comes from open query
      unapproved: openQuery.isLoading, // unapproved is in open query
      unmatched: openQuery.isLoading, // unmatched is in open query
      approved: approvedQuery.isLoading, // approved comes from approved query
      hold: openQuery.isLoading, // hold is in open query
      unmapped: openQuery.isLoading || approvedQuery.isLoading || voidedQuery.isLoading, // could be from any
      void: voidedQuery.isLoading, // void comes from voided query
    }

    // Only calculate counts for queries that have finished loading
    const recalculatedCounts = { ...defaultCounts }

    // Process open query data (affects: all, unapproved, unmatched, hold)
    if (!openQuery.isLoading && openQuery.data?.rows) {
      let openRows = openQuery.data.rows
      if (assignedToMe && user?.username) {
        openRows = openRows.filter((row: any) => row.assignee?.username === user.username)
      }

      openRows.forEach((row: any) => {
        const status = row.status
        if (status !== 'approved' && status !== 'void') {
          recalculatedCounts[status] = (recalculatedCounts[status] || 0) + 1
        }
      })

      // Calculate "all" as sum of non-approved and non-voided statuses
      recalculatedCounts.all = recalculatedCounts.unapproved + recalculatedCounts.unmatched + recalculatedCounts.hold
    }

    // Apply assigned to me filter to the data before counting
    let rowsForCounting = matchQuery.data.rows
    if (assignedToMe && user?.username) {
      rowsForCounting = matchQuery.data.rows.filter((row: any) => row.assignee?.username === user.username)
    }

    // Process voided query data (affects: void)
    if (!voidedQuery.isLoading && voidedQuery.data?.rows) {
      let voidedRows = voidedQuery.data.rows
      if (assignedToMe && user?.username) {
        voidedRows = voidedRows.filter((row: any) => row.assignee?.username === user.username)
      }

      voidedRows.forEach((row: any) => {
        if (row.status === 'void') {
          recalculatedCounts.void = (recalculatedCounts.void || 0) + 1
        }
      })
    }

    // Process approved query data (affects: approved)
    if (!approvedQuery.isLoading && approvedQuery.data?.rows) {
      let approvedRows = approvedQuery.data.rows
      if (assignedToMe && user?.username) {
        approvedRows = approvedRows.filter((row: any) => row.assignee?.username === user.username)
      }

      approvedRows.forEach((row: any) => {
        if (row.status === 'approved') {
          recalculatedCounts.approved = (recalculatedCounts.approved || 0) + 1
        }
      })
    }

    // Calculate unmapped count from any available data
    const unmappedCount = combinedData.unmapped_supplier_names?.length || 0
    recalculatedCounts.unmapped = unmappedCount

    return {
      ...recalculatedCounts,
      isLoading: loadingStates,
    }
  }, [openQuery, approvedQuery, voidedQuery, combinedData.unmapped_supplier_names, assignedToMe, user?.username])

  // --- Action Handlers --- //

  // --- Return Hook Interface --- //

  return {
    // State
    dateRange,
    setDateRange,
    activeStatus,
    setActiveStatus,
    serverFilters,
    setServerFilters,
    assignedToMe,
    setAssignedToMe,
    selectedRows,
    setSelectedRows,
    isVoidedViewActive,
    setIsVoidedViewActive,

    // DateRangeFilter State
    dateFilters,
    setDateFilters,
    dayRangeStorage,
    setDayRangeStorage,

    // Default helpers for reset functionality
    getDefaultApprovedDayRangeStorage,
    getDefaultVoidedDayRangeStorage,
    getDefaultOtherTabsDayRangeStorage,
    getDefaultApprovedDateRange,
    getDefaultVoidedDateRange,
    getDefaultDateRange,

    // Computed Data
    filteredData,
    statusCounts,

    // Query State
    isLoading: matchQuery.isLoading || (matchQuery.data && !filteredData),
    error: matchQuery.error,
    refetch: matchQuery.refetch,

    // Column Filter State
    invoiceDueFilter,
    clearInvoiceDueFilter: () => {
      setInvoiceDueFilter(null)
      // Also clear from URL
      const currentParams = new URLSearchParams(searchParams.toString())
      if (currentParams.has('invoice_due_filter')) {
        currentParams.delete('invoice_due_filter')
        setSearchParams(currentParams, { replace: true })
      }
    },
  }
}
